// imports
import { FC, useCallback, useMemo, useState } from 'react';
// components
import InfiniteSelect from './InfiniteSelect';
// interfaces, constants, helper
import { HTTP_STATUS } from 'constants/index';
import { CategorySelectProps, SelectType } from 'interfaces';
import { CategoriesPayload, useFindAllCategoryForSelectorLazyQuery } from 'generated/graphql';

const CategorySelect: FC<CategorySelectProps> = ({
  name,
  title,
  value,
  isActive,
  handleChange,
  primary = true,
  ...rest
}) => {
  const [totalPages, setTotalPages] = useState<number>(0);
  const [getLoading, setGetLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<CategoriesPayload['data']>([]);

  const updatedOptions = useMemo(() => {
    if (options?.length) {
      return options?.map<SelectType>((item) => {
        const { id, name } = item || {};

        return {
          value: id,
          name: name || '',
        };
      });
    }
    return [];
  }, [options]);

  const [fetchSelectorData, { loading }] = useFindAllCategoryForSelectorLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { findAllCategories } = data;
      const { data: categories, response, pagination } = findAllCategories || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        const { totalPages: pages } = pagination || {};

        setTotalPages(pages);
        setOptions([
          ...(getLoading ? [] : options || []),
          ...((categories as CategoriesPayload['data']) ?? []),
        ]);
        setGetLoading(false);
      }
    },
    onError: () => {
      setOptions([]);
      setGetLoading(false);
    },
  });

  const fetchData = useCallback(
    async (page?: number, query?: string) => {
      if (!page) setGetLoading(true);
      await fetchSelectorData({
        variables: {
          findAllCategoriesInput: {
            ...(query && { search: query }),
            paginationOptions: { page: page || 1, limit: 10 },
            ...(typeof isActive === 'boolean' && { isActive }),
            primary,
          },
        },
      });
    },
    [fetchSelectorData, isActive, primary],
  );

  return (
    <InfiniteSelect
      name={name}
      title={title}
      value={value}
      loading={loading}
      fetchData={fetchData}
      setOptions={setOptions}
      totalPages={totalPages}
      getLoading={getLoading}
      options={updatedOptions}
      handleChange={handleChange}
      {...rest}
    />
  );
};

export default CategorySelect;
